import React, { useState } from 'react';
import { Form } from 'react-bootstrap';

import {
  getJianyingTextMetrics,
  isJianyingTextMetricOverLimit,
} from '@/features/automation/jianying/utils/jianyingSlots.js';

const getFieldId = (slotId) => (
  `jianying-text-${String(slotId).replace(/[^a-zA-Z0-9_-]/g, '-')}`
);

const JianyingTextField = ({
  disabled,
  fieldErrors,
  onChange,
  registerFieldRef,
  slot,
  value,
}) => {
  const text = String(value?.text ?? '');
  const metrics = getJianyingTextMetrics(text);
  const overLimit = isJianyingTextMetricOverLimit(slot, metrics);
  const errors = fieldErrors[slot.id] || [];
  const isMultiline = slot.runtime?.input_mode === 'multiline';
  const fieldId = getFieldId(slot.id);
  const errorId = `${fieldId}-errors`;
  const controlProps = {
    id: fieldId,
    disabled,
    value: text,
    isInvalid: errors.length > 0,
    'aria-describedby': errors.length ? errorId : undefined,
    ref: (node) => registerFieldRef(slot.id, node),
    onChange: (event) => onChange(slot.id, event.target.value),
  };

  return (
    <Form.Group className="jianying-text-field" controlId={fieldId}>
      <div className="jianying-text-field__label">
        <Form.Label>{slot.name}{slot.required ? <span aria-hidden="true"> *</span> : null}</Form.Label>
        <small>{slot.original_content ? `原文：${slot.original_content}` : '原文为空'}</small>
      </div>
      {isMultiline ? <Form.Control {...controlProps} as="textarea" rows={4} /> : <Form.Control {...controlProps} type="text" />}
      <div className="jianying-text-field__metrics" aria-live="polite">
        <span className={overLimit.characters ? 'is-over-limit' : ''}>{metrics.characterCount}/{slot.runtime?.max_length || '不限'} 字</span>
        {isMultiline ? <span className={overLimit.lines ? 'is-over-limit' : ''}>{metrics.lineCount}/{slot.runtime?.max_lines || '不限'} 行</span> : null}
      </div>
      {errors.length ? <div id={errorId} className="jianying-text-field__errors" role="alert">{errors.map((error) => <div key={error}>{error}</div>)}</div> : null}
    </Form.Group>
  );
};

const TextGroup = ({
  disabled,
  fieldErrors,
  node,
  onChange,
  registerFieldRef,
  values,
}) => {
  const [expanded, setExpanded] = useState(true);
  return (
    <section className="jianying-business-group is-text-group">
      <button type="button" className="jianying-business-group__header" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>
        <i className={`bi bi-chevron-${expanded ? 'down' : 'right'}`} />
        <i className="bi bi-collection-play" />
        <span><strong>{node.group.name}</strong><small>业务展示组</small></span>
      </button>
      {expanded ? <div className="jianying-business-group__children">
        {node.children.map((child) => child.type === 'group' ? (
          <TextGroup key={child.key} disabled={disabled} fieldErrors={fieldErrors} node={child} onChange={onChange} registerFieldRef={registerFieldRef} values={values} />
        ) : (
          <JianyingTextField key={child.key} disabled={disabled} fieldErrors={fieldErrors} onChange={onChange} registerFieldRef={registerFieldRef} slot={child.slot} value={values[child.key]} />
        ))}
      </div> : null}
    </section>
  );
};

const JianyingTextMaterialPanel = ({
  copyPresets = [],
  disabled = false,
  fieldErrors = {},
  onChange,
  onPresetChange,
  registerFieldRef,
  sections = [],
  selectedCopyPresetId = '',
  slots = [],
  values = {},
}) => {
  if (!slots.length) return null;
  const groupNodes = sections.filter((node) => node.type === 'group');
  const rootSlots = sections.filter((node) => node.type === 'slot');

  return (
    <section className="jianying-material-panel jianying-text-material-panel">
      <div className="jianying-material-panel__header">
        <span><i className="bi bi-fonts" /><strong>文字素材</strong><small>{slots.length} 个槽位</small></span>
        <Form.Group className="jianying-copy-preset-switch" controlId="jianying-copy-preset">
          <Form.Label>文案模板</Form.Label>
          <Form.Select disabled={disabled} value={selectedCopyPresetId} onChange={(event) => onPresetChange(event.target.value)}>
            {copyPresets.map((preset) => <option key={preset.id} value={preset.id}>{preset.name}{preset.is_default ? '（默认）' : ''}</option>)}
          </Form.Select>
        </Form.Group>
      </div>
      <div className="jianying-text-material-panel__content">
        {groupNodes.map((node) => <TextGroup key={node.key} disabled={disabled} fieldErrors={fieldErrors} node={node} onChange={onChange} registerFieldRef={registerFieldRef} values={values} />)}
        {rootSlots.length ? (
          <section className="jianying-business-group is-ungrouped">
            <header className="jianying-business-group__header is-static"><i className="bi bi-circle" /><span><strong>未分组素材</strong><small>直接属于模板</small></span></header>
            <div className="jianying-business-group__children jianying-text-fields-grid">
              {rootSlots.map((node) => <JianyingTextField key={node.key} disabled={disabled} fieldErrors={fieldErrors} onChange={onChange} registerFieldRef={registerFieldRef} slot={node.slot} value={values[node.key]} />)}
            </div>
          </section>
        ) : null}
      </div>
    </section>
  );
};

export default JianyingTextMaterialPanel;
